import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import ReactPlayer from 'react-player';
import useStore from '../../hooks/useStore';
import { Container, AdItem, VideoContainer, FadeTransition } from './styles';
import DefaultLeftBanner from '../../assets/images/sport_banner/sport_cheer_banner_left.png';
import DefaultRightBanner from '../../assets/images/sport_banner/sport_cheer_banner_right.png';
import { hasCachedVideoData } from '../../common/cache/mediaCache';
import CachedImage from './CachedImage';
import Url from 'environment';

const SPORT_BANNER_VERSION = '1'; // This version is used to invalidate localStorage data when the code changes
const TRANSITION_INTERVAL = 10000; // 6 seconds -> 4 seconds -> 10 seconds ( ticket NOS-878)
const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

const isVideoUrl = (url) => {
  return videoExtensions.some((ext) => url?.toLowerCase().endsWith(ext));
};

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

function isMac() {
  return navigator.userAgentData?.platform === 'macOS' || /Mac/.test(navigator.userAgent);
}

const isAndroid = () => {
  return /Android/.test(navigator.userAgent) || /Linux/.test(navigator.platform);
};

const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export const getMatchInfoFromUrl = () => {
  const pathParts = window.location.pathname.split('/');

  // Example pathParts: ["", "match", "soccer", "11538434", "cheer"]
  const sportsType = pathParts[2]; // "soccer"
  const matchId = pathParts[3]; // "11538434"

  return { sportsType, matchId };
};

const getRandomIndex = (remainingIndexes, totalBanners) => {
  if (remainingIndexes.length === 0) {
    return Array.from({ length: totalBanners }, (_, i) => i);
  }
  return remainingIndexes;
};

const getNextRandomIndex = (remainingIndexes, setRemainingIndexes, totalBanners) => {
  const updatedIndexes = getRandomIndex(remainingIndexes, totalBanners);
  const randomIdx = Math.floor(Math.random() * updatedIndexes.length);
  const nextIndex = updatedIndexes[randomIdx];
  setRemainingIndexes(updatedIndexes.filter((_, i) => i !== randomIdx));
  return nextIndex;
};

const BannerSportCheer = observer(() => {
  const [leftBannerIndex, setLeftBannerIndex] = useState(0);
  const [rightBannerIndex, setRightBannerIndex] = useState(0);
  const [sportsBannerLeft, setSportsBannerLeft] = useState([]);
  const [sportsBannerRight, setSportsBannerRight] = useState([]);
  const { bannerStore } = useStore();
  const [leftBannerStartTime, setLeftBannerStartTime] = useState(Date.now());
  const [rightBannerStartTime, setRightBannerStartTime] = useState(Date.now());
  const [leftVideoDuration, setLeftVideoDuration] = useState(0);
  const [rightVideoDuration, setRightVideoDuration] = useState(0);
  const leftVideoPlayCountRef = useRef(0);
  const rightVideoPlayCountRef = useRef(0);
  const leftLastPlayedRef = useRef(0);
  const rightLastPlayedRef = useRef(0);
  const [remainingLeftIndexes, setRemainingLeftIndexes] = useState([]);
  const [remainingRightIndexes, setRemainingRightIndexes] = useState([]);

  const [fetchingBanner, setFetchingBanner] = useState(true);

  useEffect(() => {
    setFetchingBanner(true);
    bannerStore
      .getList()
      .then(() => {})
      .finally(() => {
        setFetchingBanner(false);
      });
  }, []);

  useEffect(() => {
    setSportsBannerLeft(toJS(bannerStore.sportsBannerLeft) || []);
    setSportsBannerRight(toJS(bannerStore.sportsBannerRight) || []);
    const { sportsType, matchId } = getMatchInfoFromUrl();

    //Check if the current version is different from the stored version
    // If different, remove the localStorage items
    const currentVersion = localStorage.getItem('sports_banner_version');
    if (currentVersion !== SPORT_BANNER_VERSION) {
      const prefixLeft = 'remaining_indexes_left_';
      const prefixRight = 'remaining_indexes_right_';
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(prefixLeft) || key.startsWith(prefixRight)) {
          localStorage.removeItem(key);
          // Decrement i to account for the change in localStorage length
          i--;
        }
      }

      localStorage.setItem('sports_banner_version', SPORT_BANNER_VERSION);
    }

    if (bannerStore.sportsBannerLeft.length > 0) {
      if (
        localStorage.getItem('remaining_indexes_left_' + sportsType + '_' + matchId) &&
        JSON.parse(decodeURIComponent(localStorage.getItem('remaining_indexes_left_' + sportsType + '_' + matchId)))
          .length > 0
      ) {
        let sportsBannerLeftArr = JSON.parse(
          decodeURIComponent(localStorage.getItem('remaining_indexes_left_' + sportsType + '_' + matchId))
        );
        let randomIdx = getNextRandomIndex(remainingLeftIndexes, setRemainingLeftIndexes, sportsBannerLeftArr.length);
        let sportsBannerLeftArrCurIndex = sportsBannerLeftArr.filter((_, i) => i === randomIdx);
        setSportsBannerLeft(sportsBannerLeftArrCurIndex);
        let sportsBannerLeftArrIndex = sportsBannerLeftArr.filter((_, i) => i !== randomIdx);
        localStorage.setItem(
          'remaining_indexes_left_' + sportsType + '_' + matchId,
          JSON.stringify(sportsBannerLeftArrIndex)
        );
      } else {
        let sportsBannerLeftArr = toJS(bannerStore.sportsBannerLeft);
        let randomIdx = getNextRandomIndex(
          remainingLeftIndexes,
          setRemainingLeftIndexes,
          bannerStore.sportsBannerLeft.length
        );
        let sportsBannerLeftArrCurIndex = sportsBannerLeftArr.filter((_, i) => i === randomIdx);
        setSportsBannerLeft(sportsBannerLeftArrCurIndex);
        let sportsBannerLeftArrIndex = sportsBannerLeftArr.filter((_, i) => i !== randomIdx);
        localStorage.setItem(
          'remaining_indexes_left_' + sportsType + '_' + matchId,
          JSON.stringify(sportsBannerLeftArrIndex)
        );
      }
    }
    if (bannerStore.sportsBannerRight.length > 0) {
      if (
        localStorage.getItem('remaining_indexes_right_' + sportsType + '_' + matchId) &&
        JSON.parse(decodeURIComponent(localStorage.getItem('remaining_indexes_right_' + sportsType + '_' + matchId)))
          .length > 0
      ) {
        let sportsBannerRightArr = JSON.parse(
          decodeURIComponent(localStorage.getItem('remaining_indexes_right_' + sportsType + '_' + matchId))
        );
        let randomIdx = getNextRandomIndex(
          remainingRightIndexes,
          setRemainingRightIndexes,
          sportsBannerRightArr.length
        );
        let sportsBannerRightArrCurIndex = sportsBannerRightArr.filter((_, i) => i === randomIdx);
        setSportsBannerRight(sportsBannerRightArrCurIndex);
        let sportsBannerRightArrIndex = sportsBannerRightArr.filter((_, i) => i !== randomIdx);
        localStorage.setItem(
          'remaining_indexes_right_' + sportsType + '_' + matchId,
          JSON.stringify(sportsBannerRightArrIndex)
        );
      } else {
        let sportsBannerRightArr = toJS(bannerStore.sportsBannerRight);
        let randomIdx = getNextRandomIndex(
          remainingRightIndexes,
          setRemainingRightIndexes,
          bannerStore.sportsBannerRight.length
        );
        let sportsBannerRightArrCurIndex = sportsBannerRightArr.filter((_, i) => i === randomIdx);
        setSportsBannerRight(sportsBannerRightArrCurIndex);
        let sportsBannerRightArrIndex = sportsBannerRightArr.filter((_, i) => i !== randomIdx);
        localStorage.setItem(
          'remaining_indexes_right_' + sportsType + '_' + matchId,
          JSON.stringify(sportsBannerRightArrIndex)
        );
      }
    }
  }, [bannerStore.sportsBannerLeft, bannerStore.sportsBannerRight, bannerStore.isBannerLoaded]);

  useEffect(() => {
    leftVideoPlayCountRef.current = 0;
    setLeftBannerStartTime(Date.now());
  }, [leftBannerIndex]);

  useEffect(() => {
    rightVideoPlayCountRef.current = 0;
    setRightBannerStartTime(Date.now());
  }, [rightBannerIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = Date.now();

      if (sportsBannerLeft.length > 1) {
        const leftBanner = sportsBannerLeft[leftBannerIndex];
        const leftElapsedTime = (currentTime - leftBannerStartTime) / 1000;

        if (isVideoUrl(leftBanner?.imgPath)) {
          const minLoopsNeeded = Math.ceil(TRANSITION_INTERVAL / (leftVideoDuration * 1000));
          if (leftElapsedTime >= TRANSITION_INTERVAL / 1000 && leftVideoPlayCountRef.current >= minLoopsNeeded) {
            setLeftBannerIndex((prev) => (prev + 1) % sportsBannerLeft.length);
          }
        } else {
          if (leftElapsedTime >= TRANSITION_INTERVAL / 1000) {
            setLeftBannerIndex((prev) => (prev + 1) % sportsBannerLeft.length);
          }
        }
      }

      if (sportsBannerRight.length > 1) {
        const rightBanner = sportsBannerRight[rightBannerIndex];
        const rightElapsedTime = (currentTime - rightBannerStartTime) / 1000;

        if (isVideoUrl(rightBanner?.imgPath)) {
          const minLoopsNeeded = Math.ceil(TRANSITION_INTERVAL / (rightVideoDuration * 1000));
          if (rightElapsedTime >= TRANSITION_INTERVAL / 1000 && rightVideoPlayCountRef.current >= minLoopsNeeded) {
            setRightBannerIndex((prev) => (prev + 1) % sportsBannerRight.length);
          }
        } else {
          if (rightElapsedTime >= TRANSITION_INTERVAL / 1000) {
            setRightBannerIndex((prev) => (prev + 1) % sportsBannerRight.length);
          }
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [
    leftBannerIndex,
    rightBannerIndex,
    leftBannerStartTime,
    rightBannerStartTime,
    leftVideoPlayCountRef,
    rightVideoPlayCountRef,
    leftVideoDuration,
    rightVideoDuration,
    sportsBannerLeft,
    sportsBannerRight,
  ]);

  const getBannerHref = (banner) => {
    if (!banner) return getContactUsHref();

    let href = '';
    if (isIOS() && banner?.hrefIos) {
      href = banner.hrefIos;
    } else if (isAndroid() && banner?.hrefAndr) {
      href = banner.hrefAndr;
    } else if (banner?.href) {
      href = banner.href;
    }

    return isValidUrl(href) ? href : '#';
  };

  const handleBannerLeftClick = (e, banner) => {
    e.preventDefault();

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'sport_banner_left_click', {
        event_label: 'PC 웹_스포츠 응원하기 배너_왼쪽_클릭수',
        redirected_url: getBannerHref(banner),
      });
    }

    handleBannerClick(e, banner);
  };

  const handleBannerRightClick = (e, banner) => {
    e.preventDefault();

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'sport_banner_right_click', {
        event_label: 'PC 웹_스포츠 응원하기 배너_오른쪽_클릭수',
        redirected_url: getBannerHref(banner),
      });
    }

    handleBannerClick(e, banner);
  };

  const getContactUsHref = () => {
    return Url.contactUsLink || 'https://sports.nmd.co/contact-us';
  };

  const handleBannerClick = (e, banner) => {
    if (!banner) {
      let contactUsHref = getContactUsHref();

      window.open(contactUsHref, '_blank');
      return;
    }

    const href = getBannerHref(banner);
    if (href !== '#' && isValidUrl(href)) {
      window.open(href, banner?.target || '_blank');
    }
  };

  const renderBannerContent = (banner, defaultImage, isLeft) => {
    if (!banner?.imgPath) {
      if (fetchingBanner) {
        return <></>;
      }

      return (
        <img
          src={defaultImage}
          alt='banner'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          data-banner-position={isLeft ? 'sportsBannerLeft' : 'sportsBannerRight'}
        />
      );
    }

    if (isVideoUrl(banner?.imgPath)) {
      return (
        <VideoContainer>
          <div style={{ opacity: hasCachedVideoData(banner?.imgPath) ? 1 : 0 }}>
            <ReactPlayer
              url={banner?.imgPath}
              playing={true}
              controls={false}
              loop={true}
              muted={true}
              playsinline={true}
              onDuration={(duration) => {
                if (isLeft) {
                  setLeftVideoDuration(duration);
                } else {
                  setRightVideoDuration(duration);
                }
              }}
              onProgress={({ played }) => {
                if (isLeft) {
                  if (played < leftLastPlayedRef.current) {
                    leftVideoPlayCountRef.current += 1;
                  }
                  leftLastPlayedRef.current = played;
                } else {
                  if (played < rightLastPlayedRef.current) {
                    rightVideoPlayCountRef.current += 1;
                  }
                  rightLastPlayedRef.current = played;
                }
              }}
              width='100%'
              height='100%'
              style={{ backgroundColor: 'transparent', zIndex: 1 }}
              config={{
                file: {
                  attributes: {
                    controlsList: 'nodownload nofullscreen noremoteplayback',
                    disablePictureInPicture: true,
                    disableRemotePlayback: true,
                    playsInline: true,
                    'webkit-playsinline': 'true',
                  },
                },
              }}
              data-banner-position={isLeft ? 'sportsBannerLeft' : 'sportsBannerRight'}
            />
          </div>
        </VideoContainer>
      );
    }

    return (
      <CachedImage
        src={banner?.imgPath}
        alt={banner.title || 'banner'}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  };

  const currentLeftBanner = sportsBannerLeft[leftBannerIndex];
  const currentRightBanner = sportsBannerRight[rightBannerIndex];

  useEffect(() => {
    leftLastPlayedRef.current = 0;
  }, [leftBannerIndex]);

  useEffect(() => {
    rightLastPlayedRef.current = 0;
  }, [rightBannerIndex]);

  return (
    <Container>
      <AdItem
        href={getBannerHref(currentLeftBanner)}
        onClick={(e) => handleBannerLeftClick(e, currentLeftBanner)}
        target='_blank'
        id='sport_banner_left'
      >
        <FadeTransition key={leftBannerIndex}>
          {renderBannerContent(currentLeftBanner, DefaultLeftBanner, true)}
        </FadeTransition>
      </AdItem>
      <AdItem
        href={getBannerHref(currentRightBanner)}
        onClick={(e) => handleBannerRightClick(e, currentRightBanner)}
        target='_blank'
        id='sport_banner_right'
      >
        <FadeTransition key={rightBannerIndex}>
          {renderBannerContent(currentRightBanner, DefaultRightBanner, false)}
        </FadeTransition>
      </AdItem>
    </Container>
  );
});

export default BannerSportCheer;
